<script setup>
import { inject, ref, onMounted, computed, onBeforeMount, onBeforeUnmount } from 'vue';
import VehicleCard from '../components/VehicleCard.vue';
import PersonalShopperCard from '../components/PersonalShopperCard.vue';
import PageSpinner from '../components/PageSpinner.vue';
import PageUnavailable from '../components/PageUnavailable.vue';
import InformationModal from '../components/InformationModal.vue';
import InformationIcon from '../components/InformationIcon.vue';
import { getActiveOffersSummary } from '../services/digitalSalesService.js';
import { useMainStore } from '../store';

/* eslint-disable vue/no-v-html */

const adobeEvent = inject('adobeEvent');

const availableCategories = ref([]);
const availableFilters = ref([]);
const selectedFilters = ref({});
const availableOffers = ref({});
const isLoading = ref(false);
const showEcolutionBlob = ref(false); // ask emil if we still need to support this
const showPageUnavailable = ref(false);
const linkExpired = ref(false);
const showDW5Background = ref(false);
const showPersonalShopperCard = ref(false);
const store = useMainStore();
const NO_CATEGORY_ID = 'no-category';

const sortCategoriesByTitle = (categoryA, categoryB) => {
  if (categoryA.id === NO_CATEGORY_ID && categoryB.id === NO_CATEGORY_ID) return 0;
  if (categoryA.id === NO_CATEGORY_ID) return 1;
  if (categoryB.id === NO_CATEGORY_ID) return -1;

  return categoryB.title.localeCompare(categoryA.title, undefined, { numeric: true, sensitivity: 'base' });
};

const toggleFilter = (filter, header) => {
  if (selectedFilters.value[header].includes(filter)) {
    selectedFilters.value[header] = selectedFilters.value[header].filter((f) => f !== filter);
  } else {
    selectedFilters.value[header].push(filter);
  }
  adobeEvent({
    event: 'toggleFilter',
    filterInfo: {
      filterName: filter,
      filterAction: selectedFilters.value[header].includes(filter) ? 'add' : 'remove',
    },
  });
};

const toggleCategory = (e) => {
  availableCategories.value = availableCategories.value.map(category => {
    if (category.id === e.target.id) category.checked = e.target.checked;
    return category;
  });
};

const sendTracking = () => {
  adobeEvent({
    event: 'pageView',
    page: {
      pageName: 'PublicAvailableOffersView',
    },
  });
};

const fetchOfferConfigurationImages = async () => {
  let offersResponse = {};
  try {
    offersResponse = (await getActiveOffersSummary()).data;
    console.log('offers: ', offersResponse);
  } catch (error) {
    console.log('error.response: ', error.response?.data);
    if (error.response?.data?.denialReason) {
      if (error.response.data.denialReason === 'LinkExpired') {
        linkExpired.value = true;
      }
      showPageUnavailable.value = true;
    } else {
      console.error('Failed getActiveOffersSummary', error);
    }
  }

  let frameAgreementAvailable = false;

  const filterMap = new Map();
  Object.entries(offersResponse).forEach(([specTypeKey, innerOffers]) => {
    const transformedOffers = innerOffers.map(
      ({
        masterOfferId,
        misc,
        expired,
        offerValidUntilDate,
        finalized,
        isOfferNotValid = false,
        createdDate,
        sortOrder,
        imageSpecificationAlt,
        filters,
        initialPrice,
        serviceSavings,
      }) => {
        const getOfferValidDate = () => {
          if (offerValidUntilDate) {
            const date = new Date(offerValidUntilDate);
            return [
              `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getFullYear()).substring(2)}`,
            ];
          }
          return [];
        };

        let offerCategory;
        filters?.forEach(({ header, category, tags }) => {
          if (category) offerCategory = category;
          if (!filterMap.has(header)) filterMap.set(header, []);
          filterMap.get(header).push(...tags);
        });

        // Flag that a frame agreement is available among the offers
        if (misc?.frameAgreement) {
          frameAgreementAvailable = true;
        }

        const keyPoints = [
          misc?.vehicleCardHighlightsText1,
          misc?.vehicleCardHighlightsText2,
          misc?.vehicleCardHighlightsText3,
          misc?.vehicleCardHighlightsText4,
          misc?.vehicleCardHighlightsText5,
        ].filter(Boolean);

        return {
          offerId: masterOfferId,
          createdDate,
          sortOrder,
          vehicleCardImageKey: misc?.vehicleCardImageKey,
          title: misc?.vehicleCardHighlightsHeader,
          title2: misc?.vehicleCardHighlightsHeader2,
          imageUrl: 'https://cdn.digitaldesign.scania.com/icons/website_hosted/truck.svg',
          additionalInfo: misc?.vehicleCardAdditionalInfo || null,
          specialOfferBannerHeader: misc?.vehicleCardSpecialOfferHeader,
          specialOfferBannerDescription: misc?.vehicleCardSpecialOfferDescription,
          keyPoints: keyPoints.map((text) => ({
            icon: 'tick',
            translationKey: text,
          })),
          offerStatus: { expired, finalized, isOfferNotValid },
          offerData: { offerValidDate: getOfferValidDate() },
          misc,
          imageSpecificationAlt: imageSpecificationAlt?.activeOffersPage,
          filters: filters?.map(({ header, tags }) => ({
            header,
            tags: tags.map((t) => t.label),
          })),
          initialPrice,
          category: offerCategory,
          serviceSavings,
        };
      },
    );
    availableOffers.value[specTypeKey] = transformedOffers;
  });

  const sortedAvailableOffers = Object.keys(availableOffers.value).sort().reduce((acc, key) => { acc[key] = availableOffers.value[key]; return acc; }, {});
  const offerCategories = Object.values(sortedAvailableOffers).flatMap((offersArray) => offersArray.filter(o => o.category).map(o => o.category));
  const hasOffersWithoutCategory = Object.values(sortedAvailableOffers).some((offersArray) => offersArray.some(o => !o.category));
  availableCategories.value = [...new Set(offerCategories)].map((category, i) => ({ id: `category-${i + 1}`, title: category, text: `${category}Text`, checked: true }));
  if (hasOffersWithoutCategory) availableCategories.value.push({ id: NO_CATEGORY_ID, checked: true });

  store.frameAgreementAvailable = frameAgreementAvailable;

  availableFilters.value = Array.from(filterMap.entries()).map(
    ([header, tagObjects]) => {
      const tagsWithGroup = tagObjects.filter((tagObj) => tagObj.group);
      const sortedTags = tagsWithGroup.sort((a, b) => {
        const groupCompare = a.group.localeCompare(b.group);
        return groupCompare !== 0 ? groupCompare : a.label.localeCompare(b.label);
      });
      const labelsOnly = sortedTags.map((t) => t.label);
      const uniqueLabels = [...new Set(labelsOnly)];

      return {
        header,
        tags: uniqueLabels,
      };
    },
  );
  selectedFilters.value = Object.fromEntries(
    availableFilters.value.map(({ header }) => [header, []]),
  );
};

const applySelectedFilters = (offer) => {
  for (const [header, selectedTags] of Object.entries(selectedFilters.value)) {
    if (selectedTags.length === 0) continue;
    const filterObj = offer.filters?.find((f) => f.header === header);
    if (!filterObj || selectedTags.some((tag) => !filterObj.tags.includes(tag))) {
      return false;
    }
  }
  return true;
};

const hasAnyOffers = computed(() => Object.values(availableOffers.value).some(array => array.length > 0));
const hasAnyFilteredOffers = computed(() => dynamicOffersGroupsCategories.value.some((category) => category.offersGroups.some((group) => group.offers.length > 0)));
const categoryFilterOptions = computed(() => availableCategories.value.filter(({ id }) => id !== NO_CATEGORY_ID).sort(sortCategoriesByTitle));

const offerMatchesCategory = (offer, { id, title }) => {
  if (id === NO_CATEGORY_ID) return !offer.category;
  return offer.category === title;
};

const dynamicOffersGroupsCategories = computed(() => {
  const sortedAvailableOffers = Object.keys(availableOffers.value).sort().reduce((acc, key) => { acc[key] = availableOffers.value[key]; return acc; }, {});
  return availableCategories.value.filter(({ checked }) => checked).map((category) => {
    const offersGroups = Object.entries(sortedAvailableOffers).map(([specTypeKey, offersArray], index) => {
      const filteredOffers = offersArray.filter((offer) => applySelectedFilters(offer)).filter((offer) => offerMatchesCategory(offer, category));
      const serviceSavingsOffers = filteredOffers.some((offer) => !!offer.serviceSavings?.length);
      return { id: index + 1, title: specTypeKey, text: `${specTypeKey}Text`, offers: filteredOffers, serviceSavingsOffers };
    });
    return { ...category, offersGroups };
  });
});

const showFilters = computed(() => {
  return store.activeMarket?.showFiltersOnActiveOffersPage;
});

const BREAKPOINTS = {
  mobile: '(max-width: 768px)',
  tablet: '(max-width: 1366px)',
};

const PERSONAL_SHOPPER_CARD_INDEX = {
  mobile: 0,
  tablet: 1,
  desktop: 3,
};

const viewport = ref('desktop');
let mediaQueries = [];

const updateViewport = () => {
  const [mobileQuery, tabletQuery] = mediaQueries;

  if (mobileQuery.matches) {
    viewport.value = 'mobile';
    return;
  }

  viewport.value = tabletQuery.matches ? 'tablet' : 'desktop';
};

const initializeViewportListeners = () => {
  mediaQueries = Object.values(BREAKPOINTS).map((query) => window.matchMedia(query));
  mediaQueries.forEach((mediaQuery) => mediaQuery.addEventListener('change', updateViewport));
  updateViewport();
};

const removeViewportListeners = () => {
  mediaQueries.forEach((mediaQuery) => mediaQuery.removeEventListener('change', updateViewport));
  mediaQueries = [];
};

const PERSONAL_SHOPPER_CARD_KEY = 'personal-shopper-card';

const CARD_TYPES = {
  vehicle: 'vehicle',
  personalShopper: 'personalShopper',
};

const personalShopperCardIndex = computed(() => PERSONAL_SHOPPER_CARD_INDEX[viewport.value]);

const createVehicleCardItem = (offer) => ({
  type: CARD_TYPES.vehicle,
  key: `vehicle-${offer.offerId}`,
  data: offer,
});

const createPersonalShopperCardItem = () => ({
  type: CARD_TYPES.personalShopper,
  key: PERSONAL_SHOPPER_CARD_KEY,
});

const shouldIncludePersonalShopperCard = (categoryIndex, groupIndex) => (
  showPersonalShopperCard.value && categoryIndex === 0 && groupIndex === 0
);

const buildCardItems = (offers, categoryIndex, groupIndex) => {
  const items = offers.map(createVehicleCardItem);

  if (!shouldIncludePersonalShopperCard(categoryIndex, groupIndex)) return items;

  const insertIndex = Math.min(personalShopperCardIndex.value, items.length);
  items.splice(insertIndex, 0, createPersonalShopperCardItem());

  return items;
};

const offersGroupsCategoriesWithCardItems = computed(() => (
  dynamicOffersGroupsCategories.value
    .filter((category) => category.offersGroups.some((group) => group.offers.length > 0))
    .sort(sortCategoriesByTitle)
    .map((category, categoryIndex) => ({
      ...category,
      offersGroups: category.offersGroups
        .filter((group) => group.offers.length > 0)
        .map((group, groupIndex) => ({
          ...group,
          cardItems: buildCardItems(group.offers, categoryIndex, groupIndex),
        })),
    }))
));

const getInitialPrice = (card) => (
  card.misc.vehicleCardShowPrice && card.initialPrice?.price ? card.initialPrice : null
);

onMounted(async () => {
  initializeViewportListeners();
  isLoading.value = true;
  sendTracking();
  await fetchOfferConfigurationImages();
  isLoading.value = false;
});

onBeforeUnmount(() => {
  removeViewportListeners();
});

onBeforeMount(() => {
  store.activePage = 'ACTIVE_OFFERS';
  store.showYearEndOfferBanner = false;
  setFeatureToggle();
});

const setFeatureToggle = async () => {
  showDW5Background.value = false;
  const response = store.featureToggleData[`${store.market}#webapp_show_availableOffersPage_DW5_background`];
  showDW5Background.value = response.isActive;
  showPersonalShopperCard.value = false;
  const response2 = store.featureToggleData[`${store.market}#webapp_show_availableOffersPage_personalShopperCard`];
  showPersonalShopperCard.value = response2.isActive;
};
</script>

<template>
  <template v-if="isLoading">
    <PageSpinner />
  </template>
  <template v-else>
    <template v-if="showPageUnavailable">
      <PageUnavailable :link-expired="linkExpired" />
    </template>
    <template v-else-if="!hasAnyOffers">
      <div class="no-active-offers">
        <h1>{{ $i18n('noActiveOffers') }}</h1>
        <p>{{ $i18n('noActiveOffersText') }}</p>
      </div>
    </template>
    <template v-else>
      <div class="active-offers__container">
        <div
          class="sdds-u-relative"
          :class="showDW5Background ? `active-offers__header__${store.market}__DW5` : 'active-offers__header'"
        >
          <div class="active-offers__header-content">
            <div>
              <p class="sdds-paragraph-01">
                {{ $i18n('availableOffersHeroTitle') }}
              </p>
              <p
                class="sdds-expressive-headline-01"
                v-html="$i18n('availableOffersHeroHeader')"
              />
              <p
                class="sdds-paragraph-02"
                v-html="$i18n('availableOffersHeroBody')"
              />
            </div>
            <template v-if="showEcolutionBlob">
              <div class="ecolution-blob">
                <h2 class="sdds-u-m0">
                  {{ $i18n('availableOffersOfferTitle') }}
                </h2>
                <p class="sdds-u-m0">
                  {{ $i18n('availableOffersOfferDescription') }}
                </p>
              </div>
            </template>
          </div>
        </div>
        <div class="active-offers__main-content">
          <template v-if="showFilters">
            <h3
              v-if="availableFilters.length || categoryFilterOptions.length > 1"
              :class="`sdds-headline-02 sdds-u-mt1 sdds-u-mb3 filter-headline-${store.market}`"
            >
              {{ $i18n('filter') }}
            </h3>
            <div
              v-if="availableFilters.length"
              class="available-filters-container"
            >
              <template
                v-for="{ header, tags } in availableFilters"
                :key="header"
              >
                <div
                  v-if="tags.length > 1 && Object.values(availableOffers).some(array => array.length > 1)"
                  class="available-filter-group"
                >
                  <sdds-dropdown
                    :placeholder="$i18n(header)"
                    type="multiselect"
                    style="--sdds-dropdown-bg: #F9FAFB;"
                    @:select-option="(e) => toggleFilter(e.target.value, header)"
                  >
                    <sdds-dropdown-option
                      v-for="tag in tags"
                      :key="tag"
                      :value="tag"
                      class="sdds-dropdown-option-background"
                    >
                      {{ $i18n(tag) }}
                    </sdds-dropdown-option>
                  </sdds-dropdown>
                </div>
              </template>
            </div>
            <div
              v-if="categoryFilterOptions.length > 1"
              class="available-categories-container"
            >
              <label class="sdds-form-label">
                {{ $i18n('leasingOptions') }}
              </label>
              <template
                v-for="{ id, title, checked } in categoryFilterOptions"
                :key="id"
              >
                <div class="sdds-checkbox-item">
                  <input
                    :id="id"
                    type="checkbox"
                    class="sdds-form-input"
                    :checked="checked"
                    @change="toggleCategory"
                  >
                  <label
                    :for="id"
                    class="sdds-form-label"
                  >
                    {{ $i18n(title) }}
                  </label>
                </div>
              </template>
            </div>
          </template>
          <template v-if="hasAnyFilteredOffers">
            <template
              v-for="category in offersGroupsCategoriesWithCardItems"
              :key="category.id"
            >
              <div
                v-if="category.id !== NO_CATEGORY_ID"
                class="category-container"
              >
                <p class="sdds-headline-01">
                  {{ $i18n(category.title) }}
                </p>
                <div :id="`modal-${category.id}`">
                  <a
                    href="#"
                    @click.prevent
                  >{{ $i18n('moreInformationText') }}</a>
                  <!-- <InformationIcon /> -->
                </div>
              </div>
              <template
                v-for="group in category.offersGroups"
                :key="group.id"
              >
                <div>
                  <div>
                    <p :class="`sdds-headline-02 sdds-u-mb0 group-title-${store.market}`">
                      {{ $i18n(group.title) }}
                    </p>
                    <p class="sdds-paragraph-03 sdds-u-mb0">
                      {{ $i18n(group.text) }}
                    </p>
                  </div>
                  <!-- Hiding below service savings banner. To Do: If not needed later, then below banner code block can be removed -->
                  <div
                    v-if="false && group.serviceSavingsOffers"
                    class="service-savings-banner sdds-banner sdds-banner-info el-margin-top-16"
                  >
                    <div class="sdds-banner-body sdds-u-pr0">
                      <div class="sdds-banner-subheader service-savings-banner-text">
                        {{ $i18n('serviceSavingsAvailable') }}
                      </div>
                      <div
                        :id="`modal-${category.id}-${group.title}`"
                        :data-analytics="`{'eventComponent':'PublicAvailableOffersView','eventType':'modal','eventLabel':'Service savings information'}`"
                        :data-dd-action-name="`Service savings information`"
                        style="cursor: pointer;"
                        class="sdds-u-pl1"
                      >
                        <!-- <a
                          href="#"
                          data-dd-action-name="more information"
                          @click.prevent
                        >{{ $i18n('moreInformationText') }}</a> -->
                        <InformationIcon
                          view-box="0 0 30 30"
                          :view-box-style="{ width: '5rem', color: '#0B1320' }"
                        />
                      </div>
                    </div>
                    <InformationModal
                      :id="`modal-${category.id}-${group.title}`"
                      :information-text="$i18n('serviceSavingsInformation')"
                    />
                  </div>
                  <div
                    class="active-offers__card-list sdds-u-mt0 sdds-u-mb3"
                    :class="`active-offers__card-list__${store.market}`"
                  >
                    <template
                      v-for="item in group.cardItems"
                      :key="item.key"
                    >
                      <PersonalShopperCard
                        v-if="item.type === CARD_TYPES.personalShopper"
                        :title="$i18n('personalShopperCardTitle')"
                        :description="$i18n('personalShopperCardDescription')"
                        :personal-shopper-url="$i18n('personalShopperCardLink')"
                        :button-text="$i18n('personalShopperCardButtonText')"
                      />
                      <VehicleCard
                        v-else
                        :offer-id="item.data.offerId"
                        :service-savings="item.data.serviceSavings || []"
                        :title="item.data.title"
                        :title2="item.data.title2"
                        :keypoints="item.data.keyPoints"
                        :vehicle-card-image-key="item.data.vehicleCardImageKey"
                        :image-url="item.data.imageUrl"
                        :alt-image="item.data.imageSpecificationAlt"
                        :offer-status="item.data.offerStatus"
                        :offer-data="item.data.offerData"
                        :card-chips="item.data.misc.cardChips || {}"
                        :card-blobs="item.data.misc.cardBlobs || {}"
                        :card-background="item.data.misc.cardBackground"
                        :additional-info="item.data.additionalInfo"
                        :special-offer-banner-header="item.data.specialOfferBannerHeader"
                        :special-offer-banner-description="item.data.specialOfferBannerDescription"
                        :initial-price="getInitialPrice(item.data)"
                        :offer-category="category.title"
                      />
                    </template>
                  </div>
                </div>
              </template>
              <InformationModal
                v-if="category.id !== NO_CATEGORY_ID"
                :id="`modal-${category.id}`"
                :title="$i18n(category.title)"
                :information-text="$i18n(category.text)"
              />
            </template>
          </template>
          <template v-else>
            <div class="no-filtered-offers">
              <h1>{{ $i18n('noFilteredOffers') }}</h1>
              <p>{{ $i18n('noFilteredOffersText') }}</p>
            </div>
          </template>
        </div>
      </div>
    </template>
  </template>
</template>

<style scoped>
.sdds-expressive-headline-01 {
  margin: unset;
  line-height: 1.2;
}
.active-offers__card-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--sdds-spacing-element-32);
  width: 100%;
  margin-top: var(--sdds-spacing-element-16);
}
.active-offers__card-list__pl {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.active-offers__card-list__de {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.active-offers__card-list__ch {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.active-offers__card-list__jp {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.active-offers__card-list > * {
  width: auto;
  margin: 0;
}
.active-offers__container {
  display: flex;
  flex-direction: column;
}
.active-offers__header {
  background-image: url('@/assets/active-offers-bg.jpeg');
  background-repeat: no-repeat;
  background-position: 60% 60%;
  background-size: cover;
  min-height: 40vh;
  color: white;
}
.active-offers__header > div {
  display: flex;
  flex-direction: column;
  width: 60%;
  margin: var(--sdds-spacing-element-24) var(--sdds-spacing-element-48);
}
.ecolution-blob {
  position: absolute;
  display: inline-block;
  right: 150px;
  bottom: -130px;
  width: 228px !important;
  height: 228px !important;
  border-radius: 50%;
  border: 2px solid #C84A07F2;
  background: linear-gradient(
      0deg,
      rgba(200, 74, 7, 0.95),
      rgba(200, 74, 7, 0.95)
    ),
    linear-gradient(
      0deg,
      rgba(217, 86, 16, 0.95),
      rgba(217, 86, 16, 0.95)
    );
  text-align: center;
  padding-top: 65px;
}
.ecolution-blob p {
  line-height: 1.3em;
}
.active-offers__header__pl__DW5 {
  background-image: url('@/assets/NEWHeroCutOrginalMedium - compact.jpg');
  background-repeat: no-repeat;
  background-position: unset;
  background-size: cover;
  min-height: 40vh;
  color: white;
}
.active-offers__header__pl__DW5 .sdds-paragraph-02 {
  max-width: 55%;
}
.active-offers__header__de__DW5 {
  background-image: url('@/assets/Hero_blue_Germany_1.png');
  background-repeat: no-repeat;
  background-position: unset;
  background-size: cover;
  min-height: 50vh;
  color: #fff;

  /* background-image: url('@/assets/super-11-hero.jpeg');
  background-repeat: no-repeat;
  background-position: unset;
  background-size: cover;
  min-height: 45vh;
  color: #000; */

  /* background-image: url('@/assets/Hero_image_DS_Germany_20260529.png'); */
  /* background-repeat: no-repeat; */
  /* /* background-position: unset; */
  /* background-position: center; */
  /* min-height: 50vh; */
  /* color: white; */
}
.active-offers__header__de__DW5 .sdds-paragraph-02 {
  max-width: 55%;
}
.active-offers__header-content {
  display: flex;
  flex-direction: column;
  min-height: 36vh;
  justify-content: space-between;
  margin: var(--sdds-spacing-element-4) var(--sdds-spacing-element-48);
}
.active-offers__main-content {
  display: flex;
  flex-direction: column;
  margin: var(--sdds-spacing-element-24) var(--sdds-spacing-element-48);
}
.filter-headline-ch {
  font-size: 20px !important;
  font-weight: normal !important;
  margin-bottom: var(--sdds-spacing-layout-16) !important;
}
.no-active-offers, .no-filtered-offers {
  display: flex;
  flex-flow: column;
  align-items: center;
}
.available-filters-container, .available-categories-container {
  display: flex !important;
  flex-direction: row !important;
  gap: var(--sdds-spacing-element-16);
  align-items: center;
}
.group-title-ch {
  font-size: 48px !important;
}
.available-filter-group {
  display: inline-flex;
}
.available-filter-group .sdds-dropdown-multiselect {
  width: 300px;
}
.available-filter-group .sdds-dropdown-multiselect .sdds-dropdown-option-background {
  background-color: #F9FAFB;
}
.category-container {
  display: flex;
  gap: 1em;
  align-items: baseline;
  margin: 3em 0 1em;
}
.category-container .sdds-headline-01 {
  margin: 0;
  font-family: "Scania Sans", arial, helvetica, sans-serif;
}
.sdds-checkbox-item {
  padding-right: var(--sdds-spacing-element-16);
}
.sdds-checkbox-item:last-of-type {
  padding-right: 0;
}

.service-savings-banner {
  background-color: #9BD9C2;
  padding: 8px 16px;
  border-radius: 4px;
  margin-bottom: var(--sdds-spacing-element-16);
}
.service-savings-banner .sdds-banner-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.service-savings-banner-text {
  color: #0B1320;
  font-size: 16px;
  font-weight: bold;
}

@media only screen and (max-width: 1366px) {
  .active-offers__card-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .group-title-ch {
    font-size: 40px !important;
  }
}
@media only screen and (max-width: 1150px) {
  .active-offers__header__pl__DW5, .active-offers__header__de__DW5 {
    background-position: 90%;
  }
}
@media only screen and (max-width: 1024px) {
  .sdds-expressive-headline-01 {
    font-size: 4em;
  }
  .active-offers__card-list > * {
    margin: unset;
  }
}
@media only screen and (max-width: 768px) {
  .group-title-ch {
    font-size: 32px !important;
  }
  .active-offers__header__pl__DW5, .active-offers__header__de__DW5 {
    min-height: unset;
  }
  .sdds-expressive-headline-01 {
    font-size: 3em;
  }
  .sdds-paragraph-02 {
    font-size: 16px;
  }
  .active-offers__header__pl__DW5 .sdds-paragraph-02 {
    max-width: unset;
  }
  .category-container {
    margin-top: 2em;
  }
  .category-container .sdds-headline-01 {
    font-size: 2em;
  }
  .sdds-headline-02 {
    font-size: 1.8em;
    margin-top: 2rem;
  }
  .active-offers__header > div {
    width: 80%;
    margin: var(--sdds-spacing-element-24) var(--sdds-spacing-element-16);
  }
  .active-offers__main-content {
    margin: var(--sdds-spacing-element-24) var(--sdds-spacing-element-16);
  }
  .active-offers__card-list {
    grid-template-columns: 1fr;
  }
  .ecolution-blob {
    width: 160px !important;
    height: 160px !important;
    bottom: -100px;
  }
}
@media only screen and (max-width: 660px) {
  .available-filters-container, .available-categories-container {
    flex-direction: column !important;
    gap: var(--sdds-spacing-element-16);
    align-items: unset;
  }
  .filter-headline-ch {
    font-size: 16px !important;
    margin-bottom: var(--sdds-spacing-layout-8) !important;
  }
  .available-categories-container {
    padding-top: var(--sdds-spacing-element-16);
  }
  .sdds-checkbox-item {
    padding-right: 0;
  }
  .category-container {
    margin-top: 1.5em;
  }
  .category-container .sdds-headline-01 {
    font-size: 1.8em;
  }
  .sdds-headline-02 {
    font-size: 1.5em;
    margin-top: 1.5rem;
  }
  .sdds-expressive-headline-01 {
    font-size: 2.5em;
  }
}
</style>
